import { HazardHubResponse, HazardHubRisks, HazardHubSummaryRow } from '../models/hazard-hub.types';
import {
  buildHazardHubJsonTabHtml,
  HAZARD_HUB_JSON_VIEWER_STYLES,
  serializeHazardHubPayload,
} from './hazard-hub-json-viewer.utils';

const POPUP_CLOSE_SVG_URL = '/assets/img/close.svg';
const HAZARD_HUB_REPORT_STORAGE_PREFIX = 'hh-report:';

export function storeHazardHubReportPayload(data: HazardHubResponse): string {
  const reportId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  sessionStorage.setItem(`${HAZARD_HUB_REPORT_STORAGE_PREFIX}${reportId}`, JSON.stringify(data));
  return reportId;
}

export function loadHazardHubReportPayload(reportId: string): HazardHubResponse | null {
  const raw = sessionStorage.getItem(`${HAZARD_HUB_REPORT_STORAGE_PREFIX}${reportId}`);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as HazardHubResponse;
  } catch {
    return null;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function getRisks(data: HazardHubResponse): HazardHubRisks {
  return (data?.risks ?? data ?? {}) as HazardHubRisks;
}

/** Summary rows for the map popup card. */
export function extractHazardHubSummary(data: HazardHubResponse): HazardHubSummaryRow[] {
  const risks = getRisks(data);
  const wildfire = risks.enhanced_wildfire;
  const composite = wildfire?.risk_scores?.composite;
  const wildfireValue =
    wildfire?.score != null
      ? composite != null
        ? `${wildfire.score} (${composite})`
        : displayValue(wildfire.score)
      : '—';

  const protectionClass =
    risks.aais_fire_protection_class_params?.class ??
    risks.hh_fpc_code?.value ??
    risks.fire_protection?.aais_proxy ??
    '—';

  const hurricane = risks.enhanced_hurricane_params;
  const hurricaneValue =
    hurricane?.score != null
      ? hurricane.text
        ? `${hurricane.score} — ${hurricane.text}`
        : displayValue(hurricane.score)
      : '—';

  const wind = risks.enhanced_wind_params;
  const windValue =
    wind?.wpctrisk ?? (wind?.score != null ? `${wind.score}${wind.text ? ` — ${wind.text}` : ''}` : '—');

  const tornado = risks.enhanced_tornado_params;
  const tornadoValue = tornado?.tpctrisk ?? (tornado?.score != null ? displayValue(tornado.score) : '—');

  const earthquake = risks.earthquake;
  const earthquakeValue = earthquake?.MMI
    ? earthquake.MMI
    : earthquake?.score != null
      ? `${earthquake.score}${earthquake.text ? ` — ${earthquake.text}` : ''}`
      : '—';

  const femaZone = risks.fema_all_flood_params?.fld_zone ?? risks.fema_all_flood?.text ?? '—';

  return [
    { label: 'Wildfire', value: wildfireValue },
    { label: 'Protection Class', value: displayValue(protectionClass) },
    { label: 'Hurricane', value: hurricaneValue },
    { label: 'Wind', value: displayValue(windValue) },
    { label: 'Tornado', value: displayValue(tornadoValue) },
    { label: 'Earthquake MMI', value: displayValue(earthquakeValue) },
    { label: 'FEMA Flood Zone', value: displayValue(femaZone) },
  ];
}

export function formatHazardHubPopupHtml(data: HazardHubResponse): string {
  const rows = extractHazardHubSummary(data);
  const body = rows
    .map(
      ({ label, value }) =>
        `<div class="sa-popup-row" role="row"><span class="sa-popup-row__label" role="rowheader">${escapeHtml(
          label,
        )}</span><span class="sa-popup-row__value">${escapeHtml(value)}</span></div>`,
    )
    .join('');

  return `<div class="sa-site-popup sa-hazard-popup">
    <div class="sa-popup-header">
      <button type="button" class="sa-popup-close" data-sa-popup-close aria-label="Close popup">
        <img src="${POPUP_CLOSE_SVG_URL}" width="24" height="24" alt="" />
      </button>
    </div>
    <div class="sa-popup-body">${body}</div>
    <div class="sa-hazard-popup-divider"></div>
    <button type="button" class="sa-hazard-full-report-btn" data-sa-hazard-full-report>Display Full HazardHub Data</button>
  </div>`;
}

function gradeColor(grade: string | null | undefined): string {
  const g = String(grade ?? '')
    .toUpperCase()
    .charAt(0);
  switch (g) {
    case 'A':
      return '#1e7a45';
    case 'B':
      return '#2e9e5a';
    case 'C':
      return '#c47a00';
    case 'D':
      return '#d96b2b';
    case 'F':
      return '#dc3e3e';
    default:
      return '#6d7275';
  }
}

function scorecardCell(label: string, score: string | null | undefined, detail: string): string {
  const grade = displayValue(score);
  const color = gradeColor(score);
  return `<div class="hh-score-cell">
    <div class="hh-score-label">${escapeHtml(label)}</div>
    <div class="hh-score-grade" style="color:${color}">${escapeHtml(grade)}</div>
    <div class="hh-score-detail">${escapeHtml(detail)}</div>
  </div>`;
}

function detailRow(label: string, value: string, valueClass = ''): string {
  return `<div class="hh-detail-row">
    <span class="hh-detail-label">${escapeHtml(label)}</span>
    <span class="hh-detail-value ${valueClass}">${escapeHtml(value)}</span>
  </div>`;
}

function formatReportTitle(risks: HazardHubRisks): string {
  const zip = risks.zip?.split('-')[0] ?? '';
  const state = risks.state ?? '';
  if (zip && state) return `Property Risk Report — ZIP ${zip}, ${state}`;
  if (risks.address) return `Property Risk Report — ${risks.address.trim()}`;
  return 'Property Risk Report';
}

function formatProximateWildland(wildfire: HazardHubRisks['enhanced_wildfire']): string {
  const areas = wildfire?.landcover_risk?.proximate_landcover_areas ?? [];
  if (!areas.length) return '—';
  return areas
    .map(a => {
      const dist = a.distance_to != null ? `${Math.round(a.distance_to)} ft` : '?';
      const pct = a.pct_wildland != null ? `${Math.round(a.pct_wildland * 100)}% wildland` : '';
      const cat = a.risk_category ?? '';
      return `${dist}: ${pct} (${cat})`.trim();
    })
    .join(' · ');
}

function formatWuiValue(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const obj = value as { text?: string; value?: string; desc?: string };
    return obj.text ?? obj.value ?? obj.desc ?? null;
  }
  return String(value);
}

function formatWuiClassification(risks: HazardHubRisks): string {
  const wuiArray = risks.wildland_urban_interface;
  if (Array.isArray(wuiArray) && wuiArray.length) {
    const attrs = wuiArray.flatMap(entry => entry.attributes ?? []);
    const classification = attrs.find(a => a.name === 'Classification')?.value;
    if (classification) return classification;
  }

  const wildfire = risks.enhanced_wildfire;
  const candidates = [
    formatWuiValue(wildfire?.wui),
    formatWuiValue(wildfire?.wui_zone),
    formatWuiValue(wildfire?.wui_indicator),
    formatWuiValue(wildfire?.wildland_urban_interface),
    formatWuiValue(risks.wildland_urban_interface),
    wildfire?.landcover_risk?.detail?.area_desc,
  ];
  const match = candidates.find(v => v != null && v !== '');
  return match ?? '—';
}

function formatWuiCode(risks: HazardHubRisks): string {
  const wuiArray = risks.wildland_urban_interface;
  if (Array.isArray(wuiArray) && wuiArray.length) {
    const attrs = wuiArray.flatMap(entry => entry.attributes ?? []);
    const categoryCode = attrs.find(a => a.name === 'Category Code')?.value;
    if (categoryCode) return categoryCode;
  }

  return formatWuiValue(risks.wildfire_housing_density?.value) ?? '—';
}

function formatPerennialWater(risks: HazardHubRisks): string {
  const pw = risks.perennial_water;
  if (!pw) return '—';
  const parts = [pw.answer, pw.text, pw.name].filter(v => v != null && v !== '');
  return parts.length ? parts.join(' — ') : '—';
}

function formatHydrantCoverage(risks: HazardHubRisks): string {
  const hydrant = risks.hydrant;
  const params = risks.hydrant_params;

  const main = hydrant?.text
    ? hydrant.text
    : hydrant?.score != null
      ? String(hydrant.score)
      : params?.distance_from_nearest_hydrant ?? '—';

  if (!params) return main;

  const extras = [
    params.distance_from_nearest_hydrant && params.distance_from_nearest_hydrant !== main
      ? `Nearest ${params.distance_from_nearest_hydrant}`
      : null,
    params.hydrants_within_1000_ft != null ? `${params.hydrants_within_1000_ft} within 1000 ft` : null,
  ].filter(Boolean);

  return extras.length ? `${main} (${extras.join(' · ')})` : main;
}

/** Full readable HazardHub report for a new browser tab. */
export function buildHazardHubReportHtml(data: HazardHubResponse): string {
  const risks = getRisks(data);
  const title = formatReportTitle(risks);
  const jurisdiction = risks.state_county?.jurisdiction ?? '';
  const stateName = risks.state_county?.state ?? risks.state ?? '';
  const lat = risks.lat != null ? risks.lat.toFixed(6) : '—';
  const lng = risks.lng != null ? risks.lng.toFixed(6) : '—';
  const matchType = risks.match_type ? `Match ${risks.match_type}` : '';
  const matchScore = risks.match_score != null ? `Score ${risks.match_score}` : '';
  const source = data.source ? `Source ${data.source}` : '';

  const drought = risks.drought;
  const droughtAlert =
    drought?.score === 'F' && drought.text
      ? `<div class="hh-alert">
          <span class="hh-alert-icon">🔥</span>
          <span><strong>Active Drought Alert:</strong> Current drought score is F — ${escapeHtml(drought.text)}${
            risks.drought_frequency_index?.pct
              ? `. Drought frequency index: ${escapeHtml(risks.drought_frequency_index.pct)} of weeks over the past 21 years.`
              : ''
          }</span>
        </div>`
      : '';

  const wildfire = risks.enhanced_wildfire;
  const hurricane = risks.enhanced_hurricane_params;
  const wind = risks.enhanced_wind_params;
  const hail = risks.enhanced_hail_params;
  const tornado = risks.enhanced_tornado_params;
  const lightning = risks.enhanced_lightning_params;
  const earthquake = risks.earthquake;
  const hhFlood = risks.enhanced_hazardhub_flood ?? risks.hazardhub_flood;
  const femaFlood = risks.fema_all_flood;
  const mold = risks.mold_index;
  const iceDam = risks.ice_dam_index;
  const frozenPipe = risks.frozen_pipe_index;
  const snowLoad = risks.hh_snow_load;
  const sinkhole = risks.sinkhole_risk_params;

  const scorecard = [
    scorecardCell(
      'Wildfire',
      wildfire?.score,
      `${wildfire?.text ?? ''} · Composite ${wildfire?.risk_scores?.composite ?? '—'}`,
    ),
    scorecardCell('Drought', drought?.score, drought?.text ?? '—'),
    scorecardCell('Wind', wind?.score, `${wind?.text ?? ''} · Scale ${wind?.scale ?? '—'}`),
    scorecardCell('Hail', hail?.score, `${hail?.text ?? ''} · Scale ${hail?.scale ?? '—'}`),
    scorecardCell('Tornado', tornado?.score, `${tornado?.text ?? ''} · Scale ${tornado?.scale ?? '—'}`),
    scorecardCell('Lightning', lightning?.score, `${lightning?.text ?? ''} · Scale ${lightning?.scale ?? '—'}`),
    scorecardCell('Hurricane', hurricane?.score, `${hurricane?.text ?? ''} · Scale ${hurricane?.scale ?? '—'}`),
    scorecardCell('Earthquake', earthquake?.score, `${earthquake?.text ?? ''} · MMI ${earthquake?.MMI ?? '—'}`),
    scorecardCell('HH Flood', hhFlood?.score, hhFlood?.text ?? '—'),
    scorecardCell('FEMA Flood', femaFlood?.score ?? 'N/A', femaFlood?.text ?? 'No DFIRM coverage'),
    scorecardCell('Mold', mold?.score, `${mold?.text ?? ''} · Index ${mold?.index ?? '—'}`),
    scorecardCell('Ice Dams', iceDam?.score, `${iceDam?.text ?? ''} · Index ${iceDam?.index ?? '—'}`),
    scorecardCell('Frozen Pipes', frozenPipe?.score, `${frozenPipe?.text ?? ''} · Index ${frozenPipe?.index ?? '—'}`),
    scorecardCell('Snow Load', snowLoad?.score, `${snowLoad?.text ?? ''} · Scale ${snowLoad?.scale ?? '—'}`),
    scorecardCell('Sinkhole', sinkhole?.sinkhole_susceptibility_grade, sinkhole?.sinkhole_susceptibility_risk ?? '—'),
    scorecardCell('Convection Storm', risks.convection_storm?.score, risks.convection_storm?.text ?? '—'),
  ].join('');

  const fireProtection = risks.fire_protection;
  const fireSuppression = risks.fire_suppression;
  const nearestStation = risks.nearest_fire_station;

  const wildfireDetails = `
    <section class="hh-section">
      <h2 class="hh-section-title">Wildfire Details</h2>
      <div class="hh-detail-grid">
        <div class="hh-detail-col">
          ${detailRow('Enhanced Score', wildfire?.score ? `${wildfire.score} — ${wildfire.text ?? ''}` : '—', 'hh-badge-warn')}
          ${detailRow('Base Risk Score', displayValue(wildfire?.risk_scores?.base))}
          ${detailRow('Composite Risk Score', displayValue(wildfire?.risk_scores?.composite))}
          ${detailRow('Landcover Risk Score', displayValue(wildfire?.landcover_risk?.score))}
          ${detailRow('Area Description', wildfire?.landcover_risk?.detail?.area_desc ?? '—')}
          ${detailRow('Site Description', wildfire?.landcover_risk?.detail?.site_desc ?? '—')}
        </div>
        <div class="hh-detail-col">
          ${detailRow('Katabatic Wind Risk', wildfire?.katabatic_wind_risk?.desc ?? '—')}
          ${detailRow('Fire Season Precip Risk', wildfire?.fire_season_precip_risk ? `Score ${wildfire.fire_season_precip_risk.score} · ${wildfire.fire_season_precip_risk.monthly_avg_precip?.toFixed(2) ?? '—'} in/mo avg` : '—')}
          ${detailRow('Wildfire Perimeter Risk', wildfire?.wildfire_perimeter_risk?.desc ?? '—')}
          ${detailRow('Veg Burn Site Risk', wildfire?.vegetation_burn_site_risk?.desc ?? '—')}
          ${detailRow('WUI Classification', formatWuiClassification(risks))}
          ${detailRow('WUI Code', formatWuiCode(risks))}
        </div>
      </div>
      ${detailRow('Proximate Wildland Areas', formatProximateWildland(wildfire))}
    </section>`;

  const fireProtectionSection = `
    <section class="hh-section">
      <h2 class="hh-section-title">Fire Protection</h2>
      <div class="hh-detail-grid">
        <div class="hh-detail-col">
          ${detailRow('Protection Score', fireProtection?.score ? `${fireProtection.score} — ${fireProtection.text ?? ''}` : '—')}
          ${detailRow('AAIS Proxy', fireProtection?.aais_proxy ?? '—')}
          ${detailRow('HH PFC Code', risks.enhanced_hh_pfs ? `${risks.enhanced_hh_pfs.value ?? ''} · Avg Loss ${((risks.enhanced_hh_pfs.average_pct_loss ?? 0) * 100).toFixed(3)}%` : '—')}
          ${detailRow('Fire Suppression Grade', fireSuppression ? `${fireSuppression.grade} — ${fireSuppression.text} (Score ${fireSuppression.score})` : '—', 'hh-badge-danger')}
          ${detailRow('Perennial Water', formatPerennialWater(risks))}
          ${detailRow('Hydrant Coverage', formatHydrantCoverage(risks))}
        </div>
        <div class="hh-detail-col">
          ${detailRow('Drive Distance (nearest)', risks.drive_distance_fire_station ? `${risks.drive_distance_fire_station.distance} ${risks.drive_distance_fire_station.units ?? 'miles'}` : '—')}
          ${detailRow('Drive Time (nearest)', risks.drive_time_fire_station ? `${risks.drive_time_fire_station.duration} ${risks.drive_time_fire_station.units ?? 'minutes'}` : '—')}
          ${detailRow('Station Type', risks.fire_station_type?.value ?? '—')}
          ${detailRow('Stations within 5 mi', displayValue(risks.number_of_fire_stations_within_5_miles))}
          ${detailRow('Stations within 10 mi', displayValue(risks.number_of_fire_stations_within_10_miles))}
          ${detailRow('Municipal Boundary', risks.urbanicity?.type ?? '—')}
        </div>
      </div>
    </section>`;

  const stationSection = nearestStation
    ? `<section class="hh-section">
        <h2 class="hh-section-title">Nearest Fire Stations</h2>
        <div class="hh-station-card">
          <div class="hh-station-name">${escapeHtml(nearestStation.title ?? '—')}</div>
          <div class="hh-station-meta">Response Area: ${escapeHtml(nearestStation.response_area ?? '—')} · ${escapeHtml(nearestStation.type ?? '—')}</div>
          <div class="hh-station-distance">
            <span>${nearestStation.drive_duration_value ?? '—'} ${nearestStation.drive_duration_units ?? 'min'} · ${nearestStation.distance?.toFixed(2) ?? '—'} mi straight</span>
            <span class="hh-station-drive">${nearestStation.drive_distance_fire_station?.distance?.toFixed(2) ?? '—'} mi drive</span>
          </div>
        </div>
      </section>`
    : '';

  const styles = `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: Inter, Lato, system-ui, sans-serif;
      background: #f0f3f8;
      color: #2f353a;
      padding: 32px;
      line-height: 1.5;
    }
    .hh-wrap {
      max-width: 1100px;
      margin: 0 auto;
      background: #fff;
      border: 1px solid #d2d4d5;
      border-radius: 8px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
      padding: 24px 28px 32px;
    }
    .hh-badge {
      display: inline-block;
      background: #265dc3;
      color: #fff;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      padding: 4px 10px;
      border-radius: 4px;
      margin-bottom: 12px;
    }
    h1 {
      font-family: Inter, Lato, system-ui, sans-serif;
      font-size: 24px;
      font-weight: 600;
      color: #2f353a;
      margin-bottom: 8px;
    }
    .hh-meta { color: #6d7275; font-size: 14px; margin-bottom: 20px; }
    .hh-meta span + span::before { content: ' · '; }
    .hh-alert {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      background: #fef2f2;
      border: 1px solid #f5c6c6;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 24px;
      font-size: 14px;
      color: #2f353a;
    }
    .hh-alert strong { color: #dc3e3e; }
    .hh-alert-icon { font-size: 18px; flex-shrink: 0; }
    .hh-section { margin-bottom: 32px; padding-top: 24px; border-top: 1px solid #e4e4e8; }
    .hh-section-title {
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: #6d7275;
      margin-bottom: 16px;
    }
    .hh-scorecard { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 12px; margin-bottom: 8px; }
    .hh-score-cell {
      background: #fff;
      border: 1px solid #d2d4d5;
      border-radius: 8px;
      padding: 12px;
      transition: border-color 0.15s ease, background 0.15s ease;
    }
    .hh-score-cell:hover { background: #eef3fb; border-color: #265dc3; }
    .hh-score-label { font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #6d7275; margin-bottom: 4px; }
    .hh-score-grade { font-size: 28px; font-weight: 700; line-height: 1; margin-bottom: 4px; }
    .hh-score-detail { font-size: 11px; color: #6d7275; }
    .hh-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 12px; }
    @media (max-width: 700px) { .hh-detail-grid { grid-template-columns: 1fr; } }
    .hh-detail-col { display: flex; flex-direction: column; gap: 8px; }
    .hh-detail-row { display: flex; justify-content: space-between; gap: 16px; padding: 8px 0; border-bottom: 1px solid #e4e4e8; font-size: 14px; }
    .hh-detail-label { color: #6d7275; flex-shrink: 0; font-weight: 500; }
    .hh-detail-value { text-align: right; color: #2f353a; }
    .hh-badge-warn { color: #c47a00; font-weight: 600; }
    .hh-badge-danger { color: #dc3e3e; font-weight: 600; }
    .hh-station-card { background: #eef3fb; border: 1px solid #d2d4d5; border-radius: 8px; padding: 16px; }
    .hh-station-name { font-size: 16px; font-weight: 600; color: #2f353a; margin-bottom: 4px; }
    .hh-station-meta { font-size: 13px; color: #6d7275; margin-bottom: 8px; }
    .hh-station-distance { display: flex; justify-content: space-between; font-size: 13px; color: #2f353a; }
    .hh-station-drive { color: #265dc3; font-weight: 600; }
    ${HAZARD_HUB_JSON_VIEWER_STYLES}
  `;

  const metaParts = [
    jurisdiction && stateName ? `${jurisdiction}, ${stateName}` : '',
    `Lat ${lat}`,
    `Lng ${lng}`,
    matchType,
    matchScore,
    source,
  ].filter(Boolean);

  const reportBody = `
    ${droughtAlert}
    <section class="hh-section" style="border-top:none;padding-top:0">
      <h2 class="hh-section-title">Risk Scorecard</h2>
      <div class="hh-scorecard">${scorecard}</div>
    </section>
    ${wildfireDetails}
    ${fireProtectionSection}
    ${stationSection}`;

  const serializedPayload = serializeHazardHubPayload(data);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(title)}</title>
  <style>${styles}</style>
</head>
<body>
  <div class="hh-wrap">
    <div class="hh-badge">HAZARDHUB</div>
    <h1>${escapeHtml(title)}</h1>
    <div class="hh-meta">${metaParts.map(p => `<span>${escapeHtml(p)}</span>`).join('')}</div>
    <div class="hh-tabs" role="tablist">
      <button type="button" class="hh-tab active" data-tab="report" role="tab" aria-selected="true">Report</button>
      <button type="button" class="hh-tab" data-tab="json" role="tab" aria-selected="false">JSON</button>
    </div>
    <div id="tab-report" class="hh-tab-panel active" role="tabpanel">${reportBody}</div>
    ${buildHazardHubJsonTabHtml(serializedPayload)}
  </div>
</body>
</html>`;
}

export function openHazardHubReportWindow(data: HazardHubResponse): void {
  const reportId = storeHazardHubReportPayload(data);
  const url = `${location.origin}/hazardhub/report/${reportId}`;
  window.open(url, '_blank');
}
